import axios, { AxiosError, AxiosResponse } from "axios";
import i18next from "i18next";
import JSZip from "jszip";
import { toast } from "react-toastify";

import { getFileExtension } from "src/utils/files-utils/get-file-extension";
import { v4 as uuidv4, NIL } from "uuid";
import { getAuthToken } from "../../custom-hooks/useDecodedToken/useDecodedToken";
import { LoadResponse } from "../../models/api/load/load.response";
import { Page } from "../../models/api/page";
import { PropsFilter } from "../../models/api/props-filter";
import { SaveResponse } from "../../models/api/save/save.response";
import { ListSortDirection, Sort } from "../../models/api/sort";
import { DocumentRef } from "../../models/documents";
import { DocumentFileDTO } from "../../models/documents/document-files";
import { DocumentFileRef } from "../../models/documents/document-files";
import { DocumentFileSTO } from "../../models/documents/document-files";
import { DownloadedDocumentFile } from "../../models/documents/document-files";
import { DocumentFilesFilter } from "../../models/documents/document-files/document-files.filter";

import { toastifyResponse } from "../../utils/toastify-response/toastify-response";
import { BaseApiService } from "../base/base-api.service";

export class DocumentFilesApiService extends BaseApiService<DocumentFileDTO, DocumentFileSTO> {
  protected apiURL = `${import.meta.env.VITE_API_URL}`;

  constructor() {
    super({
      servicePath: "/DocumentFiles",
      loadItemsPath: "/GetDocumentFiles",
      saveItemsPath: "/SaveDocumentFiles",
    });
    this.defaultSort = [
      {
        name: "name",
        direction: ListSortDirection.Ascending,
      } as Sort,
    ];
  }

  public async loadItems(): Promise<LoadResponse<DocumentFileDTO>> {
    const response = {
      id: NIL,
      invokerId: NIL,
      correlationId: NIL,
      error: "Method not used, call loadBlob() or loadDocumentsFiles() instead.",
      isCanceled: true,
      isSucceeded: false,
      items: [],
      totalItems: 0,
    } as LoadResponse<DocumentFileDTO>;

    return response;
  }

  /**
   * Create/Update/Delete items derived from STOModel, as form data
   * @param url
   * @param items
   * @param timeout
   */

  public async saveItemsForm(
    formData: FormData,
    invokerId: string | null,
    correlationId: string | null,
    timeout: number = this.defaultTimeout,
    isGenericDocument?: boolean
  ): Promise<SaveResponse> {
    const request = this.createSaveRequest([], timeout);
    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}/${
      isGenericDocument ? "SaveGenericDocumentFileContents" : "SaveDocumentFileContents"
    }`;

    formData.append("requestId", request.id);
    formData.append("invokerId", invokerId ?? request.invokerId ?? uuidv4());
    formData.append("correlationId", correlationId ?? request.correlationId ?? uuidv4());
    formData.append("timeout", "60");
    formData.append(
      "currentCompanyAccountId",
      isGenericDocument ? NIL : localStorage.getItem("currentCompanyAccountId") ?? NIL
    );

    let response = { data: { isSucceeded: false } as SaveResponse };

    try {
      response = await axios.post<SaveResponse>(url, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      const error = err as AxiosError;
      let errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      if (response.data) {
        response.data.error = errorMessage;
      }

      console.log("Failure saving file. ", errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 415: {
          errorMessage = "Wrong media type.";
          break;
        }

        case 500: {
          errorMessage = "Failure saving file - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    return response?.data;
  }

  /**
   * Download file contents by file id
   * @param id
   */
  public async loadBlob(
    id: string,
    signal?: AbortSignal,
    customURL?: string,
    customPropsFilter?: PropsFilter
  ): Promise<DownloadedDocumentFile> {
    const filter = "";
    const propsFilter: DocumentFilesFilter = {
      ...this.defaultPropsFilter,
      fileId: id,
      ...customPropsFilter,
    };
    const sorts: Sort[] = [];
    const page: Page = { pageNumber: 0, pageSize: 1 };
    const exactMatch = false;
    const timeout = this.defaultTimeout;
    const request = this.createLoadRequest(propsFilter, filter, sorts, page, exactMatch, timeout);
    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}${customURL ?? "/GetDocumentFileContents"}`;

    let response: AxiosResponse<Blob, any> = {
      data: new Blob(),
      status: 200,
      statusText: "OK",
      headers: {},
      config: {},
    };
    let errorMessage = "";
    try {
      response = await axios.post<Blob>(url, request, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
    } catch (err) {
      const error = err as AxiosError;
      errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      console.log(`${error?.response?.status ?? ""}: Failure loading file. `, errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Failure loading file - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    const { data, headers } = response;
    const contentDispositionHeader = headers["content-disposition"] ?? "";
    const fileName = this.getFileName(contentDispositionHeader);

    return { name: fileName, data, error: errorMessage };
  }

  /**
   * Download contents of files from multiple documents by document id's, as as zip archive
   * @param ids
   */
  async loadDocumentsFiles(ids: string[], isGenericDocument?: boolean): Promise<DownloadedDocumentFile> {
    const filter = "";
    const propsFilter: PropsFilter = this.defaultPropsFilter;
    propsFilter.ids = ids;
    const sorts: Sort[] = [];
    const page: Page = { pageNumber: 0, pageSize: 1000 }; // allow up to 1000 docs (each can have multiple files)
    const exactMatch = false;
    const timeout = this.defaultTimeout;
    const request = this.createLoadRequest(propsFilter, filter, sorts, page, exactMatch, timeout);
    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}/${
      isGenericDocument ? "GetGenericDocumentsFileContents" : "GetDocumentsFilesContents"
    }`;
    let response: AxiosResponse<Blob, any> = {
      data: new Blob(),
      status: 200,
      statusText: "OK",
      headers: {},
      config: {},
    };
    let errorMessage = "";

    try {
      response = await axios.post<Blob>(url, request, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
    } catch (err) {
      const error = err as AxiosError;
      errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      console.log("Failure loading document files. ", errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Failure loading document files - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }
    const { data, headers } = response;
    const contentDispositionHeader = headers["content-disposition"] ?? "";
    const fileName = this.getFileName(contentDispositionHeader);

    return { name: fileName, data, error: errorMessage };
  }

  private getFileName(contentDispositionHeader: string): string {
    const fileNameUtf8Attr = "filename*=UTF-8''";
    const fileNameAttr = "filename=";
    let fileName = "";
    if (contentDispositionHeader.indexOf(fileNameUtf8Attr) !== -1) {
      fileName = contentDispositionHeader.split(fileNameUtf8Attr)[1].split(";")[0];
      fileName = decodeURIComponent(fileName);
    } else if (contentDispositionHeader.indexOf(fileNameAttr) !== -1) {
      fileName = contentDispositionHeader.split(fileNameAttr)[1].split(";")[0];
      // Remove quotes if present and decode
      fileName = fileName.replace(/"/g, "");
      try {
        fileName = decodeURIComponent(fileName);
      } catch (e) {
        // If decoding fails, use the original filename
        console.warn("Failed to decode filename:", fileName);
      }
    }

    return fileName;
  }
}

export const makeBlobUrl = (blob: Blob) => {
  const aFile = new Blob([blob], { type: blob?.type });
  // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
  const blobUrl = URL.createObjectURL(aFile);
  return blobUrl;
};

export const downloadBlob = (blob: Blob, name = "file.txt", downloadCallback?: () => void) => {
  try {
    const aFile = new Blob([blob], { type: blob.type });
    // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
    const blobUrl = URL.createObjectURL(aFile);

    // Create a link element
    const link = document.createElement("a");

    // Set link's href to point to the Blob URL
    link.href = blobUrl;
    link.download = name;

    // Append link to the body
    document.body.appendChild(link);

    // Dispatch click event on the link
    // This is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
    downloadCallback && downloadCallback();
    // Remove link from body
    document.body.removeChild(link);
  } catch (error) {
    console.log("Failure saving file", name, error);
  }
};

export const handleDownloadFile = async (
  file: DocumentFileRef,
  customFileName?: string,
  downloadCallback?: () => void,
  isGenericDocument?: boolean
) => {
  // eslint-disable-next-line no-restricted-globals
  const confirmed = confirm(
    `${i18next.t("components.upload_document.document_files_table.download_confirmation_message")} ${file.name}?`
  );

  if (confirmed) {
    const documentFilesApiService = new DocumentFilesApiService();
    const loadPath = isGenericDocument ? "/GetGenericDocumentsFilesContents" : undefined;
    const { data, name, error }: DownloadedDocumentFile = await documentFilesApiService.loadBlob(
      file.id,
      undefined,
      loadPath
    );

    const fileExtension = getFileExtension(file.name).toLowerCase();

    error
      ? toast(error)
      : customFileName
      ? downloadBlob(data, `${customFileName}.${fileExtension}`, downloadCallback)
      : downloadBlob(data, name, downloadCallback);
  }
};

export const getDownloadedFile = async (file: DocumentFileRef, isGenericDocument?: boolean) => {
  // eslint-disable-next-line no-restricted-globals
  console.log("isGenericDocument: ", isGenericDocument);
  const documentFilesApiService = new DocumentFilesApiService();

  const customUrl = isGenericDocument ? "/GetGenericDocumentsFilesContents" : undefined;

  const { data, name, error }: DownloadedDocumentFile = await documentFilesApiService.loadBlob(
    file.id,
    undefined,
    customUrl
  );

  if (error) {
    toast(error);
  } else {
    return { data, name };
  }
};

export const handleDownloadDocsFiles = async (
  docsIds: string[],
  customDocAndFilesNames?: { customFolderName: string; customFilesName: string },
  withoutConfirmation?: boolean,
  isGenericDocument?: boolean
) => {
  const confirmed =
    withoutConfirmation ??
    window.confirm(`${i18next.t("components.upload_document.download_document_files_confirmation_message")}`);

  if (confirmed) {
    const documentFilesApiService = new DocumentFilesApiService();
    const { data, name, error }: DownloadedDocumentFile = await documentFilesApiService.loadDocumentsFiles(
      docsIds,
      isGenericDocument
    );

    if (error) {
      toast(error);
    } else {
      if (customDocAndFilesNames) {
        // Extract files from the original zip
        const originalZip = await JSZip.loadAsync(data);

        // Create a new zip with modified filenames
        const modifiedZip = new JSZip();

        // Iterate through the files in the original zip
        await Promise.all(
          Object.keys(originalZip.files).map(async (relativePath, idx) => {
            const file = originalZip.file(relativePath);

            if (file) {
              // Read the content of the file as Uint8Array
              const fileContentUint8Array = await file.async("uint8array");

              // Create a Blob from the Uint8Array
              const fileContentBlob = new Blob([fileContentUint8Array], { type: file.comment });

              // Get the file name within the folder
              const fileNameWithinFolder = relativePath.split("/").pop();

              const fileExtension = fileNameWithinFolder ? getFileExtension(fileNameWithinFolder).toLowerCase() : "";

              // Add the modified file to the new zip with the modified name and folder
              modifiedZip.file(
                `${customDocAndFilesNames.customFolderName}/${customDocAndFilesNames.customFilesName}${
                  Object.keys(originalZip.files).length > 1 ? `_${idx + 1}` : ""
                }.${fileExtension}`,
                fileContentBlob
              );
            }
          })
        );

        // Generate the modified zip as a Blob
        const modifiedBlob = await modifiedZip.generateAsync({ type: "blob" });

        const originFileExtension = getFileExtension(name).toLowerCase();

        // Download the modified zip
        downloadBlob(modifiedBlob, `${customDocAndFilesNames.customFolderName}.${originFileExtension}`);
      } else {
        downloadBlob(data, name);
      }
    }
  }
};

export const handleDeleteDocumentFile = async (file: DocumentFileRef, document: DocumentRef): Promise<SaveResponse> => {
  const response = await new DocumentFilesApiService().saveItems([{ ...file, document, isDeleted: true }]);

  toastifyResponse(response, "all", "success_deleting_file", "failure_deleting_file", "error_deleting_file");

  return response;
};
